import type { FieldValues, Path, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

type PlaceAddressFields = {
  address: string;
  lat: string;
  lng: string;
};

export function usePlaceAddressField<T extends FieldValues>(
  register: UseFormRegister<T>,
  setValue: UseFormSetValue<T>,
  getValues: UseFormGetValues<T>,
  trigger: UseFormTrigger<T>,
  fields: PlaceAddressFields,
) {
  const addressField = fields.address as Path<T>;
  const latField = fields.lat as Path<T>;
  const lngField = fields.lng as Path<T>;

  const registration = register(addressField, {
    required: 'Required',
    validate: () => {
      const values = getValues();
      const lat = values[latField] as number | undefined;
      const lng = values[lngField] as number | undefined;
      return (lat != null && lng != null) || 'Select an address from the suggestions';
    },
  });

  const handlePlaceSelect = (place: { formattedAddress: string; coordinates: { lat: number; lng: number } }) => {
    setValue(latField, place.coordinates.lat as never, { shouldDirty: true });
    setValue(lngField, place.coordinates.lng as never, { shouldDirty: true });
    setValue(addressField, place.formattedAddress as never, { shouldDirty: true });
    void trigger(addressField);
  };

  const handleAddressChange = (value: string) => {
    setValue(addressField, value as never, { shouldDirty: true });
  };

  const handleManualEdit = () => {
    setValue(latField, undefined as never, { shouldDirty: true });
    setValue(lngField, undefined as never, { shouldDirty: true });
  };

  return {
    registration,
    handlePlaceSelect,
    handleAddressChange,
    handleManualEdit,
  };
}
